import type { Lesson } from './types.js';

// =============================================================================
// SECTION 1 — Novice (A1). Pop culture themes: Harry Potter, Marvel, Pixar,
// Stranger Things, The Office, Studio Ghibli.
// =============================================================================

export const U1_LESSONS: Lesson[] = [
  {
    id: 'u1-l1',
    title: 'Say hi',
    popCulture: 'Harry Potter',
    xpReward: 10,
    exercises: [
      {
        id: 'u1-l1-e1',
        type: 'multiple_choice',
        prompt: 'Which one means "Olá"?',
        choices: ['Hello', 'Goodbye', 'Thanks', 'Sorry'],
        answerIndex: 0,
        newWord: 'Hello',
        character: 'bea',
        bubble: 'Hello! I\'m Bea — nice to meet you.',
        keyword: 'Hello',
      },
      {
        id: 'u1-l1-e2',
        type: 'translate',
        prompt: 'Translate: "Olá, Harry!"',
        from: 'pt',
        to: 'en',
        answer: 'Hello, Harry!',
        accepts: ['Hi, Harry!'],
      },
      {
        id: 'u1-l1-e3',
        type: 'multiple_choice',
        prompt: 'Choose the correct greeting for the morning.',
        choices: ['Good morning', 'Good night', 'See you', 'Goodbye'],
        answerIndex: 0,
        newWord: 'Good morning',
      },
      {
        id: 'u1-l1-e4',
        type: 'listen',
        prompt: 'Type what you hear',
        audioText: 'Hello, how are you?',
        answer: 'Hello, how are you?',
      },
      {
        id: 'u1-l1-e5',
        type: 'reorder',
        prompt: 'Build the sentence.',
        tokens: ['Hello', ',', 'my', 'friend', '!'],
        answer: ['Hello', ',', 'my', 'friend', '!'],
      },
      {
        id: 'u1-l1-e6',
        type: 'match_pairs',
        prompt: 'Match greetings.',
        pairs: [
          { left: 'Hello', right: 'Olá' },
          { left: 'Goodbye', right: 'Tchau' },
          { left: 'Good morning', right: 'Bom dia' },
          { left: 'Good night', right: 'Boa noite' },
        ],
      },
      {
        id: 'u1-l1-e7',
        type: 'listen_choice',
        prompt: 'Which one did you hear?',
        audioText: 'Good morning, Harry!',
        choices: ['Good morning, Harry!', 'Good night, Harry!', 'Hello, Harry!', 'See you, Harry!'],
        answerIndex: 0,
      },
      {
        id: 'u1-l1-e8',
        type: 'image_select',
        prompt: 'Which one is the wand?',
        promptAudio: 'Which one is the wand?',
        options: [
          { illustration: 'wand', word: 'wand' },
          { illustration: 'shield', word: 'shield' },
          { illustration: 'ring', word: 'ring' },
          { illustration: 'book', word: 'book' },
        ],
        answerIndex: 0,
      },
    ],
  },
  {
    id: 'u1-l2',
    title: 'My name is…',
    popCulture: 'The Avengers',
    xpReward: 10,
    exercises: [
      {
        id: 'u1-l2-e1',
        type: 'multiple_choice',
        prompt: '"My name is Tony" means…',
        choices: ['Meu nome é Tony', 'O nome dela é Tony', 'Você é Tony', 'Tony, eu sou'],
        answerIndex: 0,
        newWord: 'My name is',
      },
      {
        id: 'u1-l2-e2',
        type: 'translate',
        prompt: 'Translate: "I am Steve."',
        from: 'en',
        to: 'pt',
        answer: 'Eu sou Steve.',
        accepts: ['Eu sou o Steve.'],
      },
      {
        id: 'u1-l2-e3',
        type: 'reorder',
        prompt: 'Build the sentence.',
        tokens: ['My', 'name', 'is', 'Natasha', '.'],
        answer: ['My', 'name', 'is', 'Natasha', '.'],
      },
      {
        id: 'u1-l2-e4',
        type: 'fill_blank',
        prompt: 'Fill the blank: "___ name is Bruce."',
        answer: 'My',
      },
      {
        id: 'u1-l2-e5',
        type: 'speak',
        prompt: 'Say your name out loud.',
        target: 'My name is Peter.',
      },
      {
        id: 'u1-l2-e6',
        type: 'listen',
        prompt: 'Type what you hear',
        audioText: 'Nice to meet you.',
        answer: 'Nice to meet you.',
      },
    ],
  },
  {
    id: 'u1-l3',
    title: 'Where are you from?',
    popCulture: 'Stranger Things',
    xpReward: 15,
    exercises: [
      {
        id: 'u1-l3-e1',
        type: 'multiple_choice',
        prompt: '"I am from Hawkins" — what does "from" mean?',
        choices: ['de', 'para', 'em', 'com'],
        answerIndex: 0,
        newWord: 'from',
      },
      {
        id: 'u1-l3-e2',
        type: 'translate',
        prompt: 'Translate: "Eu sou do Brasil."',
        from: 'pt',
        to: 'en',
        answer: 'I am from Brazil.',
        accepts: ['I\'m from Brazil.'],
      },
      {
        id: 'u1-l3-e3',
        type: 'reorder',
        prompt: 'Build the sentence.',
        tokens: ['Where', 'are', 'you', 'from', '?'],
        answer: ['Where', 'are', 'you', 'from', '?'],
      },
      {
        id: 'u1-l3-e4',
        type: 'fill_blank',
        prompt: '"I ___ from Hawkins."',
        answer: 'am',
      },
      {
        id: 'u1-l3-e5',
        type: 'match_pairs',
        prompt: 'Match countries.',
        pairs: [
          { left: 'Brazil', right: 'Brasil' },
          { left: 'Japan', right: 'Japão' },
          { left: 'England', right: 'Inglaterra' },
          { left: 'Italy', right: 'Itália' },
        ],
      },
      {
        id: 'u1-l3-e6',
        type: 'listen_choice',
        prompt: 'Which country did you hear?',
        audioText: 'I am from Brazil.',
        choices: ['I am from Brazil.', 'I am from Japan.', 'I am from England.', 'I am from Italy.'],
        answerIndex: 0,
      },
      {
        id: 'u1-l3-e7',
        type: 'speak',
        prompt: 'Introduce yourself.',
        target: 'I am from Brazil and I love Stranger Things.',
      },
    ],
  },
];

export const U2_LESSONS: Lesson[] = [
  {
    id: 'u2-l1',
    title: 'Numbers 1–10',
    popCulture: 'Demon Slayer',
    xpReward: 10,
    exercises: [
      {
        id: 'u2-l1-e1',
        type: 'multiple_choice',
        prompt: '"Three" means…',
        choices: ['Três', 'Dois', 'Cinco', 'Oito'],
        answerIndex: 0,
        newWord: 'three',
      },
      {
        id: 'u2-l1-e2',
        type: 'match_pairs',
        prompt: 'Match numbers (like the Hashira ranks).',
        pairs: [
          { left: 'One', right: '1' },
          { left: 'Five', right: '5' },
          { left: 'Nine', right: '9' },
          { left: 'Ten', right: '10' },
        ],
      },
      {
        id: 'u2-l1-e3',
        type: 'fill_blank',
        prompt: 'Tanjiro has ___ siblings alive at first (only one sister).',
        answer: 'one',
      },
      {
        id: 'u2-l1-e4',
        type: 'listen',
        prompt: 'Type what you hear.',
        audioText: 'Seven demons, three friends.',
        answer: 'Seven demons, three friends.',
      },
      {
        id: 'u2-l1-e5',
        type: 'reorder',
        prompt: 'Build: "I have two swords."',
        tokens: ['I', 'have', 'two', 'swords', '.'],
        answer: ['I', 'have', 'two', 'swords', '.'],
      },
    ],
  },
  {
    id: 'u2-l2',
    title: 'Colors',
    popCulture: 'Pixar — Inside Out',
    xpReward: 10,
    exercises: [
      {
        id: 'u2-l2-e1',
        type: 'multiple_choice',
        prompt: 'Joy is…',
        choices: ['Yellow', 'Blue', 'Red', 'Purple'],
        answerIndex: 0,
        newWord: 'yellow',
      },
      {
        id: 'u2-l2-e2',
        type: 'multiple_choice',
        prompt: 'Anger is…',
        choices: ['Red', 'Green', 'White', 'Pink'],
        answerIndex: 0,
      },
      {
        id: 'u2-l2-e3',
        type: 'match_pairs',
        prompt: 'Match colors to their feelings.',
        pairs: [
          { left: 'Blue', right: 'Sadness' },
          { left: 'Yellow', right: 'Joy' },
          { left: 'Green', right: 'Disgust' },
          { left: 'Red', right: 'Anger' },
        ],
      },
      {
        id: 'u2-l2-e4',
        type: 'translate',
        prompt: 'Translate: "O céu é azul."',
        from: 'pt',
        to: 'en',
        answer: 'The sky is blue.',
      },
      {
        id: 'u2-l2-e5',
        type: 'listen',
        prompt: 'Type what you hear.',
        audioText: 'My favorite color is green.',
        answer: 'My favorite color is green.',
      },
    ],
  },
  {
    id: 'u2-l3',
    title: 'Days of the week',
    popCulture: 'Friends',
    xpReward: 15,
    exercises: [
      {
        id: 'u2-l3-e1',
        type: 'multiple_choice',
        prompt: 'Ross\'s favorite day:',
        choices: ['Saturday', 'Monday', 'Tuesday', 'Friday'],
        answerIndex: 3,
        newWord: 'Friday',
      },
      {
        id: 'u2-l3-e2',
        type: 'reorder',
        prompt: 'Build: "I see you on Sunday."',
        tokens: ['I', 'see', 'you', 'on', 'Sunday', '.'],
        answer: ['I', 'see', 'you', 'on', 'Sunday', '.'],
      },
      {
        id: 'u2-l3-e3',
        type: 'match_pairs',
        prompt: 'Match days.',
        pairs: [
          { left: 'Monday', right: 'Segunda' },
          { left: 'Wednesday', right: 'Quarta' },
          { left: 'Saturday', right: 'Sábado' },
          { left: 'Sunday', right: 'Domingo' },
        ],
      },
      {
        id: 'u2-l3-e4',
        type: 'fill_blank',
        prompt: '"We meet on ___" — after Thursday.',
        answer: 'Friday',
      },
    ],
  },
];

export const U3_LESSONS: Lesson[] = [
  {
    id: 'u3-l1',
    title: 'The family',
    popCulture: 'The Incredibles',
    xpReward: 15,
    exercises: [
      {
        id: 'u3-l1-e1',
        type: 'multiple_choice',
        prompt: 'Violet\'s father is Mr. Incredible. "Father" means…',
        choices: ['Pai', 'Mãe', 'Irmão', 'Avô'],
        answerIndex: 0,
        newWord: 'father',
      },
      {
        id: 'u3-l1-e2',
        type: 'match_pairs',
        prompt: 'Match family members.',
        pairs: [
          { left: 'Father', right: 'Pai' },
          { left: 'Mother', right: 'Mãe' },
          { left: 'Brother', right: 'Irmão' },
          { left: 'Sister', right: 'Irmã' },
        ],
      },
      {
        id: 'u3-l1-e3',
        type: 'translate',
        prompt: 'Translate: "This is my sister."',
        from: 'en',
        to: 'pt',
        answer: 'Esta é minha irmã.',
        accepts: ['Essa é minha irmã.'],
      },
      {
        id: 'u3-l1-e4',
        type: 'fill_blank',
        prompt: 'Jack-Jack is the ___ (baby) of the family.',
        answer: 'baby',
      },
      {
        id: 'u3-l1-e5',
        type: 'reorder',
        prompt: 'Build: "I love my family."',
        tokens: ['I', 'love', 'my', 'family', '.'],
        answer: ['I', 'love', 'my', 'family', '.'],
      },
    ],
  },
  {
    id: 'u3-l2',
    title: 'Daily routine',
    popCulture: 'The Office (US)',
    xpReward: 15,
    exercises: [
      {
        id: 'u3-l2-e1',
        type: 'multiple_choice',
        prompt: 'Jim\'s routine: "I ___ coffee every morning."',
        choices: ['drink', 'eat', 'write', 'break'],
        answerIndex: 0,
        newWord: 'drink',
      },
      {
        id: 'u3-l2-e2',
        type: 'reorder',
        prompt: 'Build: "I work at the office."',
        tokens: ['I', 'work', 'at', 'the', 'office', '.'],
        answer: ['I', 'work', 'at', 'the', 'office', '.'],
      },
      {
        id: 'u3-l2-e3',
        type: 'translate',
        prompt: 'Translate: "Michael bebe café de manhã."',
        from: 'pt',
        to: 'en',
        answer: 'Michael drinks coffee in the morning.',
      },
      {
        id: 'u3-l2-e4',
        type: 'listen',
        prompt: 'Type what you hear.',
        audioText: 'I wake up at seven.',
        answer: 'I wake up at seven.',
      },
    ],
  },
];

export const U4_LESSONS: Lesson[] = [
  {
    id: 'u4-l1',
    title: 'At the Three Broomsticks',
    popCulture: 'Harry Potter',
    xpReward: 15,
    exercises: [
      {
        id: 'u4-l1-e1',
        type: 'multiple_choice',
        prompt: 'Harry orders: "I ___ a butterbeer, please."',
        choices: ['want', 'am', 'do', 'go'],
        answerIndex: 0,
        newWord: 'want',
      },
      {
        id: 'u4-l1-e2',
        type: 'reorder',
        prompt: 'Build: "The food is good."',
        tokens: ['The', 'food', 'is', 'good', '.'],
        answer: ['The', 'food', 'is', 'good', '.'],
      },
      {
        id: 'u4-l1-e3',
        type: 'match_pairs',
        prompt: 'Match food items.',
        pairs: [
          { left: 'Bread', right: 'Pão' },
          { left: 'Water', right: 'Água' },
          { left: 'Apple', right: 'Maçã' },
          { left: 'Cheese', right: 'Queijo' },
        ],
      },
      {
        id: 'u4-l1-e4',
        type: 'fill_blank',
        prompt: 'Ron says: "I ___ hungry!" (estou com fome)',
        answer: 'am',
      },
      {
        id: 'u4-l1-e5',
        type: 'listen',
        prompt: 'Type what you hear.',
        audioText: 'I want some water, please.',
        answer: 'I want some water, please.',
      },
    ],
  },
  {
    id: 'u4-l2',
    title: 'Studio Ghibli feast',
    popCulture: 'Spirited Away',
    xpReward: 15,
    exercises: [
      {
        id: 'u4-l2-e1',
        type: 'multiple_choice',
        prompt: 'Chihiro\'s parents ate too much. "To eat" means…',
        choices: ['Comer', 'Beber', 'Dormir', 'Correr'],
        answerIndex: 0,
        newWord: 'to eat',
      },
      {
        id: 'u4-l2-e2',
        type: 'translate',
        prompt: 'Translate: "I like rice."',
        from: 'en',
        to: 'pt',
        answer: 'Eu gosto de arroz.',
      },
      {
        id: 'u4-l2-e3',
        type: 'reorder',
        prompt: 'Build: "She drinks tea every day."',
        tokens: ['She', 'drinks', 'tea', 'every', 'day', '.'],
        answer: ['She', 'drinks', 'tea', 'every', 'day', '.'],
      },
      {
        id: 'u4-l2-e4',
        type: 'speak',
        prompt: 'Order food.',
        target: 'I would like some tea, please.',
      },
    ],
  },
];

export const U5_LESSONS: Lesson[] = [
  {
    id: 'u5-l1',
    title: 'My home',
    popCulture: 'Toy Story',
    xpReward: 20,
    exercises: [
      {
        id: 'u5-l1-e1',
        type: 'multiple_choice',
        prompt: 'Woody lives in Andy\'s ___.',
        choices: ['bedroom', 'car', 'school', 'park'],
        answerIndex: 0,
        newWord: 'bedroom',
      },
      {
        id: 'u5-l1-e2',
        type: 'match_pairs',
        prompt: 'Match rooms.',
        pairs: [
          { left: 'Bedroom', right: 'Quarto' },
          { left: 'Kitchen', right: 'Cozinha' },
          { left: 'Living room', right: 'Sala' },
          { left: 'Bathroom', right: 'Banheiro' },
        ],
      },
      {
        id: 'u5-l1-e3',
        type: 'translate',
        prompt: 'Translate: "My house is big."',
        from: 'en',
        to: 'pt',
        answer: 'Minha casa é grande.',
      },
      {
        id: 'u5-l1-e4',
        type: 'fill_blank',
        prompt: 'Buzz is ___ the toy box.',
        answer: 'in',
      },
      {
        id: 'u5-l1-e5',
        type: 'listen',
        prompt: 'Type what you hear.',
        audioText: 'There is a cat in the kitchen.',
        answer: 'There is a cat in the kitchen.',
      },
    ],
  },
];
